import type { AppProps, NextWebVitalsMetric } from 'next/app';
import { ApolloProvider } from '@apollo/client';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/next';

// Third Party
import { GoogleOAuthProvider } from '@react-oauth/google';

// Utils
import { useApollo } from '@utils/apollo-react-client';
import { reportClientSideNavigation } from '@utils/google-tag-manger';
import getConfig from '@config';

// UI
import 'react-dates/lib/css/_datepicker.css'; // main css file
import '../styles/globals.css';
import { NotificationProvider } from '@contexts/use-notification';
import { GoogleTagManagerScript } from '@components/google-tag-manager';
import '@fortawesome/fontawesome-svg-core/styles.css';
import { config } from '@fortawesome/fontawesome-svg-core';
import { SessionProvider } from 'next-auth/react';
import { AnalyticsProvider } from '@contexts/use-analytics';
import { roobert, ceraStencil } from '../fonts';
import { SessionHandler } from '@components/auth/session-handler';

/**
 * The wrapper for all pages. Provides a place for site-wide functionality, such as error handling,
 * persisting data, maintaining state between pages, etc.
 *
 * NOTE: Do not add HTML output here. Use a layout component instead
 *
 * @see https://nextjs.org/docs/advanced-features/custom-app
 */
const App: React.FC<AppProps> = ({ Component, pageProps }) => {
  // Prevent fontawesome from dynamically adding its css since we did it manually above
  config.autoAddCss = false;

  const apolloClient = useApollo(pageProps);

  return (
    <div className={`${roobert.variable} ${ceraStencil.variable} font-sans`}>
      <SpeedInsights />
      <Analytics />
      <GoogleOAuthProvider
        clientId={getConfig().publicRuntimeConfig.google.clientId}
      >
        <ApolloProvider client={apolloClient}>
          <NotificationProvider>
            <SessionProvider>
              <AnalyticsProvider>
                <SessionHandler />
                <noscript>
                  <p
                    data-nosnippet="true"
                    style={{ fontSize: '14px', textAlign: 'center' }}
                  >
                    We&apos;re sorry, some parts of the Savvy website don&apos;t
                    work properly without JavaScript enabled.
                  </p>
                </noscript>
                <Component {...pageProps} />
              </AnalyticsProvider>
            </SessionProvider>
          </NotificationProvider>
        </ApolloProvider>
      </GoogleOAuthProvider>
      <GoogleTagManagerScript />
    </div>
  );
};

export default App;
export function reportWebVitals(metric: NextWebVitalsMetric) {
  switch (metric.name) {
    case 'Next.js-hydration':
      // during page load, right after hydration
      // allow GTM to handle this case
      break;
    case 'Next.js-route-change-to-render':
      // after client-side transitions
      reportClientSideNavigation(window.location.href);
      break;
    default:
      break;
  }
}
